import { Playbook } from '@/types/playbook/playbook';
import { AlertTriangle, Bot, CheckCircle, Search, Settings, User } from 'lucide-react';

export const proactiveProblemSolver: Playbook = {
  meta: {
    id: 'proactive-problem-solver',
    slug: 'proactive-problem-solver',
    categoryId: 'provide-support',
    name: 'The Proactive Problem-Solver',
    tagline: 'Solve issues before users even ask.',
    description: 'Detect when users are struggling with errors or form issues and proactively offer help to resolve problems before frustration sets in.',
    strategy: 'Detect when users are struggling (e.g., error pages, form validation issues) and proactively offer help to resolve the problem.',
    benefits: [
      'Prevent user frustration',
      'Reduce abandonment rates',
      'Improve user experience',
      'Catch issues early'
    ],
    targetAudience: ['E-commerce sites', 'SaaS platforms', 'Financial services'],
    estimatedSetupTime: '20 minutes',
    difficulty: 'intermediate',
    tags: ['proactive', 'error-detection', 'problem-solving', 'user-experience'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Proactive Problem-Solver Playbook - Smart Error Detection & Resolution',
    metaDescription: 'Detect user struggles and errors automatically. Deploy a proactive Genie to solve problems before users get frustrated.',
    keywords: ['proactive support', 'error detection', 'problem solver', 'user frustration', 'Genie assistance']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "I noticed you encountered an error. Let me help you resolve this quickly. What were you trying to do?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "I noticed you hit an error. Can I help?" },
    { speaker: 'user', message: "Yes, the payment form won't submit" },
    { speaker: 'agent', message: "I see the issue - your card expired last month. Would you like to update it?" },
    { speaker: 'user', message: "Oh! Yes please" },
    { speaker: 'agent', message: "I'll guide you to the payment settings page now." }
  ],
  templateConfig: {
    firstMessage: "I noticed you might be having trouble. Can I help resolve this?",
    systemPrompt: "You are a proactive problem-solving Genie. Detect user frustration or errors and offer immediate assistance. Be empathetic and solution-focused.",
    tools: ['error_detection', 'navigation_guide', 'knowledge_base_search']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'User encounters error',
      description: 'User hits error page, form validation issue, or shows signs of struggle'
    },
    {
      type: 'genie',
      icon: AlertTriangle,
      genieIcon: 'genie-lamp',
      label: 'Detects problem instantly',
      description: 'Genie monitors user behavior and automatically detects errors or frustration patterns'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'settings',
      label: 'Proactively offers help',
      description: 'Genie immediately appears with contextual assistance before user asks'
    },
    {
      type: 'action',
      icon: Search,
      genieIcon: 'settings',
      label: 'Diagnoses root cause',
      description: 'Analyzes error context and user journey to identify the specific problem'
    },
    {
      type: 'action',
      icon: Settings,
      genieIcon: 'settings',
      label: 'Guides resolution',
      description: 'Provides step-by-step guidance or direct links to fix the issue'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Problem resolved quickly',
      description: 'Issue resolved before frustration builds, improving satisfaction and retention'
    }
  ],
  metrics: {
    conversionRate: '23%',
    responseTime: 'Immediate',
    satisfactionScore: '4.7/5',
    costSavings: '$35k/month',
    timeToValue: '<1 minute'
  },
  testimonials: [
    {
      companyName: 'CheckoutPro',
      authorName: 'Lisa Wang',
      authorTitle: 'UX Director',
      quote: 'Cart abandonment dropped 35% since we added proactive problem solving. Users love getting help before they get frustrated.',
      metrics: '35% reduction in cart abandonment'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'Error Detection Setup',
      description: 'How to configure intelligent error detection and triggers',
      url: '/docs/error-detection-setup'
    },
    {
      type: 'checklist',
      title: 'Common Error Scenarios',
      description: 'List of typical user problems to monitor for proactive intervention',
      url: '/checklists/common-errors'
    }
  ]
};